import { useRef, useEffect, useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Eraser } from "lucide-react";

const SCALE = 3; // internal-resolution multiplier for crisp, non-pixelated output
const DISPLAY_W = 500;
const DISPLAY_H = 140;
const WIDTH = DISPLAY_W * SCALE;
const HEIGHT = DISPLAY_H * SCALE;

export const SignaturePad = ({ onChange, testId = "signature" }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [typed, setTyped] = useState("");
  const [mode, setMode] = useState("draw");

  const getCtx = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    return ctx;
  };

  const clearCanvas = () => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
  };

  useEffect(() => {
    clearCanvas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: (clientX - rect.left) * (WIDTH / rect.width),
      y: (clientY - rect.top) * (HEIGHT / rect.height),
    };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    const ctx = getCtx();
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineWidth = 6;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#111827";
  };

  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = getCtx();
    const { x, y } = pos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    emitDrawn();
  };

  const emitDrawn = () => {
    onChange?.(canvasRef.current.toDataURL("image/png"));
  };

  const handleClearDraw = () => {
    clearCanvas();
    onChange?.("");
  };

  const renderTyped = (value) => {
    setTyped(value);
    if (!value.trim()) {
      onChange?.("");
      return;
    }
    const c = document.createElement("canvas");
    c.width = WIDTH;
    c.height = HEIGHT;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, WIDTH, HEIGHT);
    ctx.fillStyle = "#111827";
    ctx.font = `${48 * SCALE}px 'Brush Script MT', 'Segoe Script', cursive`;
    ctx.textBaseline = "middle";
    ctx.fillText(value, 15 * SCALE, HEIGHT / 2);
    onChange?.(c.toDataURL("image/png"));
  };

  return (
    <div data-testid={testId}>
      <Tabs value={mode} onValueChange={setMode}>
        <TabsList className="grid grid-cols-2 w-48 mb-2">
          <TabsTrigger value="draw" data-testid={`${testId}-tab-draw`}>Draw</TabsTrigger>
          <TabsTrigger value="type" data-testid={`${testId}-tab-type`}>Type</TabsTrigger>
        </TabsList>
        <TabsContent value="draw">
          <div className="relative">
            <canvas
              ref={canvasRef}
              width={WIDTH}
              height={HEIGHT}
              data-testid={`${testId}-canvas`}
              className="w-full border-2 border-dashed border-slate-300 rounded-lg bg-white touch-none cursor-crosshair"
              style={{ aspectRatio: `${WIDTH}/${HEIGHT}` }}
              onMouseDown={start}
              onMouseMove={move}
              onMouseUp={end}
              onMouseLeave={end}
              onTouchStart={start}
              onTouchMove={move}
              onTouchEnd={end}
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleClearDraw}
              data-testid={`${testId}-clear`}
              className="absolute top-2 right-2 text-slate-400 hover:text-slate-700"
            >
              <Eraser className="w-4 h-4 mr-1" /> Clear
            </Button>
          </div>
          <p className="text-xs text-slate-400 mt-1">Sign above with your finger or mouse.</p>
        </TabsContent>
        <TabsContent value="type">
          <Input
            value={typed}
            onChange={(e) => renderTyped(e.target.value)}
            placeholder="Type your full name"
            data-testid={`${testId}-typed-input`}
          />
          {typed && (
            <div
              className="mt-2 border-2 border-dashed border-slate-300 rounded-lg bg-white px-4 py-3"
              style={{ fontFamily: "'Brush Script MT','Segoe Script',cursive", fontSize: 36, color: "#111827" }}
            >
              {typed}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SignaturePad;
